/**
 * Implement a class that represents a Hugging Face Inference client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import { InferenceClient, type TextGenerationStreamOutput } from "@huggingface/inference";
import { Message } from "../message";
import { Thread } from "../thread";
import { traceable } from "../tracing";
import { BaseModelDriver } from "./base";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;

/**
 * Available Hugging Face models for text generation
 * These are just examples of popular models - there are thousands available
 */
export type HuggingFaceModel =
  | "meta-llama/Llama-3.1-8B-Instruct"
  | "meta-llama/Llama-3.1-70B-Instruct"
  | "mistralai/Mistral-7B-Instruct-v0.3"
  | "mistralai/Mixtral-8x7B-Instruct-v0.1"
  | "tiiuae/falcon-7b-instruct"
  | "google/flan-t5-xxl";

/**
 * Configuration options for the Hugging Face driver
 */
export interface HuggingFaceConfig {
  /**
   * Hugging Face API Token
   */
  apiToken?: string;

  /**
   * Model to use
   * @default "meta-llama/Llama-3.1-8B-Instruct"
   */
  model?: HuggingFaceModel | string;

  /**
   * Custom endpoint URL (if using a custom deployed endpoint)
   */
  endpointUrl?: string;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Enable tracing for this driver
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;

  /**
   * Provider to use
   * @default "cerebras"
   */
  provider?: string;
}

/**
 * Represents a Hugging Face Inference client and provides tools for converting messages to/from the LLM
 */
export class HuggingFaceThreadDriver extends BaseModelDriver {
  /**
   * Hugging Face Inference Client
   */
  private client: InferenceClient;

  /**
   * Custom endpoint client if provided
   */
  private endpointClient?: any; // Using any to avoid type issues with the endpoint client

  /**
   * Model to use
   */
  private model: string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;

  /**
   * Traced version of textGeneration 
   */
  private textGenerationWithTracing: (params: any) => Promise<any>;

  /**
   * Traced version of textGenerationStream
   */
  private textGenerationStreamWithTracing: (params: any) => Promise<any>;

  /**
   * Traced version of endpoint textGeneration
   */
  private endpointTextGenerationWithTracing?: (params: any) => Promise<any>;

  /**
   * Traced version of endpoint textGenerationStream
   */
  private endpointTextGenerationStreamWithTracing?: (params: any) => Promise<any>;

  /**
   * Store provider if specified
   */
  private provider: string;

  /**
   * Private methods for chat completion with tracing
   */
  private chatCompletionWithTracing: (params: any) => Promise<any>;
  private chatCompletionStreamWithTracing: (params: any) => Promise<any>;
  private endpointChatCompletionWithTracing?: (params: any) => Promise<any>;
  private endpointChatCompletionStreamWithTracing?: (params: any) => Promise<any>;

  /**
   * Returns a list of example Hugging Face models
   */
  static getExampleModels(): HuggingFaceModel[] {
    return [
      "meta-llama/Llama-3.1-8B-Instruct",
      "meta-llama/Llama-3.1-70B-Instruct",
      "mistralai/Mistral-7B-Instruct-v0.3",
      "mistralai/Mixtral-8x7B-Instruct-v0.1",
      "tiiuae/falcon-7b-instruct",
      "google/flan-t5-xxl"
    ];
  }

  /**
   * Create a new Hugging Face driver
   * @param config Configuration options
   */
  constructor(config: HuggingFaceConfig = {}) {
    super();

    // Get API Key from config or environment variable
    const apiToken = config.apiToken || process.env.HUGGINGFACE_API_TOKEN;
    if (!apiToken) {
      throw new Error(
        "Hugging Face API Token must be provided in config or as HUGGINGFACE_API_TOKEN environment variable"
      );
    }

    // Create base client
    this.client = new InferenceClient(apiToken);
    
    // Store provider if specified
    this.provider = config.provider || "cerebras";
    
    // Set configuration options
    this.model = config.model || "meta-llama/Llama-3.1-8B-Instruct";
    this.temperature = config.temperature ?? 0.7;
    this.maxTokens = config.maxTokens;
    this.cache = config.cache ?? false;
    
    // Initialize tracing methods with no-op functions that just call the original methods
    this.textGenerationWithTracing = async (params: any) => this.client.textGeneration(params);
    this.textGenerationStreamWithTracing = async (params: any) => this.client.textGenerationStream(params);
    this.chatCompletionWithTracing = async (params: any) => this.client.chatCompletion(params);
    this.chatCompletionStreamWithTracing = async (params: any) => this.client.chatCompletionStream(params);
    
    // Add tracing if enabled
    if (config.trace ?? false) {
      this.setupTracing(config.traceMetadata);
    }

    // Create endpoint client if URL is provided
    if (config.endpointUrl) {
      this.endpointClient = this.client.endpoint(config.endpointUrl);
    }
  }

  /**
   * Setup tracing for the Hugging Face client
   */
  private setupTracing(traceMetadata?: Record<string, unknown>): void {
    // Instead of overriding methods directly, we'll create wrapper methods
    // that we'll use instead of calling the client methods directly
    
    // Create wrapped versions of the methods that include tracing
    this.textGenerationWithTracing = (params: any) => {
      return traceable(
        async (params: any) => {
          return await this.client.textGeneration(params);
        },
        {
          run_type: "llm",
          name: "huggingface",
          ...traceMetadata,
          aggregator: (args: any[], result: any) => {
            const params = args[0] || {};
            return {
              model: params.model,
              input: {
                inputs: params.inputs,
                parameters: params.parameters,
              },
              output: result.generated_text,
              finish_reason: "stop"
            };
          }
        }
      )(params);
    };
    
    this.textGenerationStreamWithTracing = (params: any) => {
      return traceable(
        async (params: any) => {
          return await this.client.textGenerationStream(params);
        },
        {
          run_type: "llm",
          name: "huggingface_stream",
          ...traceMetadata,
          aggregator: (args: any[]) => {
            const params = args[0] || {};
            return {
              model: params.model,
              input: {
                inputs: params.inputs,
                parameters: params.parameters,
              },
              streaming: true
            };
          }
        }
      )(params);
    };
    
    // Add traced versions of chat completion methods
    this.chatCompletionWithTracing = (params: any) => {
      return traceable(
        async (params: any) => {
          return await this.client.chatCompletion(params);
        },
        {
          run_type: "llm",
          name: "huggingface_chat",
          ...traceMetadata,
          aggregator: (args: any[], result: any) => {
            const params = args[0] || {};
            return {
              model: params.model,
              provider: params.provider,
              input: {
                messages: params.messages,
                max_tokens: params.max_tokens,
                temperature: params.temperature,
              },
              output: result.choices?.[0]?.message?.content,
              finish_reason: result.choices?.[0]?.finish_reason || "stop"
            };
          }
        }
      )(params);
    };
    
    this.chatCompletionStreamWithTracing = (params: any) => {
      return traceable(
        async (params: any) => {
          return await this.client.chatCompletionStream(params);
        },
        {
          run_type: "llm",
          name: "huggingface_chat_stream",
          ...traceMetadata,
          aggregator: (args: any[]) => {
            const params = args[0] || {};
            return {
              model: params.model,
              provider: params.provider,
              input: {
                messages: params.messages,
                max_tokens: params.max_tokens,
                temperature: params.temperature,
              },
              streaming: true
            };
          }
        }
      )(params);
    };
    
    // If endpoint client exists, do the same for it
    if (this.endpointClient) {
      this.endpointTextGenerationWithTracing = (params: any) => {
        return traceable(
          async (params: any) => {
            return await this.endpointClient.textGeneration(params);
          },
          {
            run_type: "llm",
            name: "huggingface_endpoint",
            ...traceMetadata,
            aggregator: (args: any[], result: any) => {
              const params = args[0] || {};
              return {
                endpoint: "custom",
                input: {
                  inputs: params.inputs,
                  parameters: params.parameters,
                },
                output: result?.generated_text,
                finish_reason: "stop"
              };
            }
          }
        )(params);
      };
      
      this.endpointTextGenerationStreamWithTracing = (params: any) => {
        return traceable(
          async (params: any) => {
            return await this.endpointClient.textGenerationStream(params);
          },
          {
            run_type: "llm",
            name: "huggingface_endpoint_stream",
            ...traceMetadata,
            aggregator: (args: any[]) => {
              const params = args[0] || {};
              return {
                endpoint: "custom",
                input: {
                  inputs: params.inputs,
                  parameters: params.parameters,
                },
                streaming: true
              };
            }
          }
        )(params);
      };
      
      // Add endpoint chat completion methods with tracing
      this.endpointChatCompletionWithTracing = (params: any) => {
        return traceable(
          async (params: any) => {
            return await this.endpointClient.chatCompletion(params);
          },
          {
            run_type: "llm",
            name: "huggingface_endpoint_chat",
            ...traceMetadata,
            aggregator: (args: any[], result: any) => {
              const params = args[0] || {};
              return {
                endpoint: "custom",
                provider: params.provider,
                input: {
                  messages: params.messages,
                  max_tokens: params.max_tokens,
                  temperature: params.temperature,
                },
                output: result.choices?.[0]?.message?.content,
                finish_reason: result.choices?.[0]?.finish_reason || "stop"
              };
            }
          }
        )(params);
      };
      
      this.endpointChatCompletionStreamWithTracing = (params: any) => {
        return traceable(
          async (params: any) => {
            return await this.endpointClient.chatCompletionStream(params);
          },
          {
            run_type: "llm",
            name: "huggingface_endpoint_chat_stream",
            ...traceMetadata,
            aggregator: (args: any[]) => {
              const params = args[0] || {};
              return {
                endpoint: "custom",
                provider: params.provider,
                input: {
                  messages: params.messages,
                  max_tokens: params.max_tokens,
                  temperature: params.temperature,
                },
                streaming: true
              };
            }
          }
        )(params);
      };
    }
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    this.debug("Sending message to Hugging Face API", { model: this.model, provider: this.provider });

    try {
      let response;
      
      // Check if we're using the Cerebras provider which requires chatCompletion
      if (this.provider === "cerebras") {
        // Format messages for chat completion API
        const messages = this.formatMessagesForChatAPI(thread);
        
        // Create parameters for the chat completion API call
        const params: any = {
          model: this.model,
          messages,
          temperature: this.temperature,
          provider: this.provider,
          tool_choice: "auto",
          parallel_tool_calls: false
        };
        
        // Add max tokens if specified
        if (this.maxTokens) {
          params.max_tokens = this.maxTokens;
        }
        
        // Use either the endpoint client or the main client with tracing if enabled
        if (this.endpointClient && this.endpointChatCompletionWithTracing) {
          response = await this.endpointChatCompletionWithTracing(params);
        } else if (this.chatCompletionWithTracing) {
          response = await this.chatCompletionWithTracing(params);
        } else {
          // Fall back to normal client if tracing is not set up
          const clientToUse = this.endpointClient || this.client;
          response = await clientToUse.chatCompletion(params);
        }
        
        // Get the response content
        const responseContent = response.choices?.[0]?.message?.content || "";
        
        console.log("RESPONSE from LLM:", responseContent);
        
        // Create a new message from the response
        const message = new Message({
          role: "assistant",
          content: responseContent,
          cache: this.cache,
        });
        
        // Look for tool calls in the response for models that don't natively support tool calling
        console.log("Checking for tool calls in response...");
        const toolCalls = this.parseToolCallsFromText(responseContent);
        console.log("Tool calls found:", toolCalls ? toolCalls.length : 0);
        
        if (toolCalls && toolCalls.length > 0) {
          // Display each tool call
          toolCalls.forEach((call, index) => {
            console.log(`Tool call ${index + 1}:`, call);
          });
          
          // Create a new message with the tool calls
          const updatedMessage = new Message({
            role: "assistant",
            content: responseContent,
            toolCalls: toolCalls.map(call => ({
              toolUseId: `tool-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
              name: call.name,
              arguments: call.arguments
            })),
            cache: this.cache,
          });
          
          // Replace the message
          Object.assign(message, updatedMessage);
          
          // Add the message to the thread
          thread.push(message);
          
          // Manually trigger the processToolCalls method
          console.log("Executing tool calls now...");
          await this.executeToolCalls(thread, toolCalls);
        } else {
          // Add the message to the thread
          thread.push(message);
        }
      } else {
        // For other providers, use text generation
        // Format the conversation for the model
        const prompt = this.formatMessagesForAPI(thread);
        
        // Check for potential token issues
        if (prompt.length > MAX_PROMPT_CHARACTERS) {
          throw new Error(`The thread content is too long (${prompt.length} characters). Try a shorter conversation.`);
        }

        // Create parameters for the API call
        const params: any = {
          model: this.model,
          inputs: prompt,
          parameters: {
            temperature: this.temperature,
            return_full_text: false
          },
          provider: this.provider
        };

        // Add max tokens if specified
        if (this.maxTokens) {
          params.parameters.max_new_tokens = this.maxTokens;
        }

        // Use either the endpoint client or the main client with tracing if enabled
        if (this.endpointClient && this.endpointTextGenerationWithTracing) {
          response = await this.endpointTextGenerationWithTracing(params);
        } else if (this.textGenerationWithTracing) {
          response = await this.textGenerationWithTracing(params);
        } else {
          // Fall back to normal client if tracing is not set up
          const clientToUse = this.endpointClient || this.client;
          response = await clientToUse.textGeneration(params);
        }

        // Create a new message from the response
        const message = new Message({
          role: "assistant",
          content: response.generated_text,
          cache: this.cache,
        });
        
        // Look for tool calls in the response
        const toolCalls = this.parseToolCallsFromText(response.generated_text);
        if (toolCalls && toolCalls.length > 0) {
          // Create a new message with the tool calls
          const updatedMessage = new Message({
            role: "assistant",
            content: response.generated_text,
            toolCalls: toolCalls.map(call => ({
              toolUseId: `tool-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
              name: call.name,
              arguments: call.arguments
            })),
            cache: this.cache,
          });
          
          // Replace the message
          Object.assign(message, updatedMessage);
          
          // Add the message to the thread
          thread.push(message);
          
          // Manually trigger the processToolCalls method
          await this.executeToolCalls(thread, toolCalls);
        } else {
          // Add the message to the thread
          thread.push(message);
        }
      }

      return thread;
    } catch (error) {
      this.error("Error sending message to Hugging Face API", { error });
      throw error;
    }
  }

  /**
   * Execute tool calls for the Hugging Face driver
   * This is needed because the standard isToolCall check isn't detecting our tool calls
   */
  private async executeToolCalls(thread: Thread, toolCalls: { name: string, arguments: any }[]): Promise<void> {
    const tools = thread.getTools();
    
    console.log("Found tools:", tools.map(t => t.getName()));
    console.log("Tool calls to execute:", toolCalls.map(tc => tc.name));
    
    for (const toolCall of toolCalls) {
      const tool = tools.find(t => t.getName() === toolCall.name);
      if (tool) {
        try {
          // Process parameters - convert string numbers to actual numbers if needed
          const processedArgs = { ...toolCall.arguments };
          
          // For calculator tool, convert a and b parameters to numbers
          if (toolCall.name === "calculator") {
            if ('a' in processedArgs && typeof processedArgs.a === 'string') {
              processedArgs.a = Number(processedArgs.a);
            }
            if ('b' in processedArgs && typeof processedArgs.b === 'string') {
              processedArgs.b = Number(processedArgs.b);
            }
          }
          
          console.log(`[${toolCall.name}] Executing with args:`, processedArgs);
          
          // Execute the tool
          const toolUseId = `tool-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
          const result = await tool.executeTool(processedArgs, {
            ...toolCall,
            toolUseId,
            arguments: processedArgs
          });
          
          console.log(`[${toolCall.name}] Result:`, result);
          
          // Create a tool result message
          const userMessage = new Message({
            role: "user",
            content: "",
            toolResults: [{
              toolUseId,
              name: toolCall.name,
              result: result.success ? result.result : null,
              error: result.error
            }],
            cache: false
          });
          
          // Add the message to the thread
          thread.push(userMessage);
          
          // Generate a response from the model with the tool result
          await this.sendThread(thread);
        } catch (e) {
          console.error(`Error executing tool ${toolCall.name}:`, e);
          this.error(`Error executing tool ${toolCall.name}`, e);
        }
      } else {
        console.log(`No tool found with name: ${toolCall.name}`);
      }
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    this.debug("Streaming message to Hugging Face API", { model: this.model, provider: this.provider });

    try {
      // Create a new message to add to the thread
      const message = new Message({
        role: "assistant",
        content: "",
        cache: this.cache,
      });
      
      let streamResponse;
      
      // Check if we're using the Cerebras provider which requires chatCompletionStream
      if (this.provider === "cerebras") {
        // Format messages for chat completion API
        const messages = this.formatMessagesForChatAPI(thread);
        
        // Create parameters for the chat completion API call
        const params: any = {
          model: this.model,
          messages,
          temperature: this.temperature,
          provider: this.provider
        };
        
        // Add max tokens if specified
        if (this.maxTokens) {
          params.max_tokens = this.maxTokens;
        }
        
        // Use either the endpoint client or the main client with tracing if enabled
        if (this.endpointClient && this.endpointChatCompletionStreamWithTracing) {
          streamResponse = await this.endpointChatCompletionStreamWithTracing(params);
        } else if (this.chatCompletionStreamWithTracing) {
          streamResponse = await this.chatCompletionStreamWithTracing(params);
        } else {
          // Fall back to normal client if tracing is not set up
          const clientToUse = this.endpointClient || this.client;
          streamResponse = await clientToUse.chatCompletionStream(params);
        }
        
        // Create a streaming content generator for chat completion
        const streamContent = this.createChatStreamGenerator(streamResponse, message);
        
        // Yield the stream
        yield { stream: streamContent, message };
      } else {
        // For other providers, use text generation stream
        // Format the conversation for the model
        const prompt = this.formatMessagesForAPI(thread);
        
        // Check for potential token issues
        if (prompt.length > MAX_PROMPT_CHARACTERS) {
          throw new Error(`The thread content is too long (${prompt.length} characters). Try a shorter conversation.`);
        }

        // Create parameters for the API call
        const params: any = {
          model: this.model,
          inputs: prompt,
          parameters: {
            temperature: this.temperature,
            return_full_text: false
          },
          provider: this.provider
        };

        // Add max tokens if specified
        if (this.maxTokens) {
          params.parameters.max_new_tokens = this.maxTokens;
        }

        // Use either the endpoint client or the main client with tracing if enabled
        if (this.endpointClient && this.endpointTextGenerationStreamWithTracing) {
          streamResponse = await this.endpointTextGenerationStreamWithTracing(params);
        } else if (this.textGenerationStreamWithTracing) {
          streamResponse = await this.textGenerationStreamWithTracing(params);
        } else {
          // Fall back to normal client if tracing is not set up
          const clientToUse = this.endpointClient || this.client;
          streamResponse = await clientToUse.textGenerationStream(params);
        }

        // Create a streaming content generator
        const streamContent = this.createStreamGenerator(streamResponse, message);
        
        // Yield the stream
        yield { stream: streamContent, message };
      }

      // Add the message to the thread
      thread.push(message);

      // Return the updated thread
      return thread;
    } catch (error) {
      this.error("Error streaming message to Hugging Face API", { error });
      throw error;
    }
  }

  /**
   * Format messages from the Thread object for the Hugging Face API
   * @param thread Thread to format
   * @returns Formatted prompt string
   */
  private formatMessagesForAPI(thread: Thread): string {
    let systemPrompt = "";
    const messages: string[] = [];
    
    // Process each message in the thread
    for (const message of thread.all()) {
      if (message.role === "system") {
        // Capture system prompt separately
        systemPrompt = message.content || "";
      } else if (message.role === "user") {
        messages.push(`Human: ${message.content || ""}`);
      } else if (message.role === "assistant") {
        messages.push(`Assistant: ${message.content || ""}`);
      }
      console.log('formatMessagesForAPI', {message});
      // Include tool results in the conversation
      if (message.toolResults && message.toolResults.length > 0) {
        for (const toolResult of message.toolResults) {
          const resultStr = typeof toolResult.result === 'object' 
            ? JSON.stringify(toolResult.result, null, 2)
            : String(toolResult.result);
            
          messages.push(`Tool (${toolResult.name}): ${resultStr}`);
        }
      }
    }
    
    // Create the complete prompt with system message if available
    let fullPrompt = "";
    if (systemPrompt) {
      fullPrompt += `System: ${systemPrompt}\n\n`;
    }
    
    // Add all the messages
    fullPrompt += messages.join("\n");
    
    // Add a final line to prompt the assistant's response
    fullPrompt += "\nAssistant:";
    
    return fullPrompt;
  }

  /**
   * Format messages from the Thread object for the Chat API format
   * @param thread Thread to format
   * @returns Array of messages in chat completion format
   */
  private formatMessagesForChatAPI(thread: Thread): any[] {
    const formattedMessages: any[] = [];
    let systemMessage = null;
    
    // Get tools from the thread
    const tools = thread.getTools();
    
    // Process each message in the thread
    for (const message of thread.all()) {
      if (message.role === "system") {
        // Store system message to add at the beginning
        let systemContent = message.content || "";
        
        // For models that don't natively support tool calling,
        // append tool descriptions to the system message
        if (tools.length > 0) {
          systemContent += "\n\nYou have access to the following tools:\n";
          tools.forEach(tool => {
            systemContent += `\n- ${tool.getName()}: ${tool.getDescription()}\n`;
            
            // Add parameter info with proper parameter names based on the actual tool schema
            try {
              // Get the schema from the tool's parameters
              const schema = tool.getParameters();
              if (schema) {
                systemContent += "  Parameters:\n";
                
                // Try to extract parameter info from the schema
                try {
                  // Access the underlying schema definition
                  const schemaObj = (schema as any)._def?.shape || {};
                  
                  // Extract each parameter's information
                  Object.entries(schemaObj).forEach(([paramName, paramDef]) => {
                    // Try to get description from the parameter definition
                    const description = (paramDef as any)._def?.description || "";
                    systemContent += `  - ${paramName}: ${description}\n`;
                  });
                } catch (e) {
                  // If we can't extract parameter details, just list parameters
                  systemContent += `  Parameters: ${Object.keys((schema as any)._def?.shape || {}).join(", ")}\n`;
                }
              }
            } catch (e) {
              // Fall back to basic description if error
              systemContent += `  (See tool documentation for parameters)\n`;
            }
          });
          
          // Give clear instructions on the EXACT format to use
          systemContent += "\nTo use a tool, you MUST respond using EXACTLY this format:\n";
          systemContent += "```json\n{\"tool\": \"tool_name\", \"parameters\": {\"parameter1\": \"value1\", \"parameter2\": \"value2\", ...}}\n```\n";
          systemContent += "\nMake sure to use the exact parameter names as specified in the tool description.\n";
          systemContent += "For example, if the calculator tool has parameters 'operation', 'a', and 'b', use those exact names.\n";
        }
        
        systemMessage = {
          role: "system",
          content: systemContent
        };
      } else if (message.role === "user") {
        formattedMessages.push({
          role: "user",
          content: message.content || ""
        });
      } else if (message.role === "assistant") {
        formattedMessages.push({
          role: "assistant",
          content: message.content || ""
        });
      }

      console.log('formatMessagesForChatAPI', {message});
      console.dir(message, {depth: null});
      
      // For tool results, attach them to a user message
      if (message.toolResults && message.toolResults.length > 0) {
        const toolResultContent = message.toolResults.map(result => {
          const resultStr = typeof result.result === 'object' 
            ? JSON.stringify(result.result, null, 2)
            : String(result.result);
          return `Tool (${result.name}) result:\n${resultStr}`;
        }).join("\n\n");
        
        formattedMessages.push({
          role: "user",
          content: `${toolResultContent}`
        });
      }
    }
    
    // Add system message at the beginning if present
    if (systemMessage) {
      formattedMessages.unshift(systemMessage);
    }
    
    return formattedMessages;
  }

  /**
   * Parse tool calls from text
   */
  private parseToolCallsFromText(text: string): { name: string; arguments: Record<string, unknown> }[] | null {
    console.log("Parsing tool calls from text:", text);
    
    // First try to find JSON in markdown code blocks
    const codeBlockPattern = /```json\s*({[\s\S]*?})\s*```/g;
    let codeMatch;
    const toolCalls: { name: string; arguments: Record<string, unknown> }[] = [];
    
    while ((codeMatch = codeBlockPattern.exec(text)) !== null) {
      console.log("Found code block match:", codeMatch[0]);
      try {
        // Ensure the capture group exists
        if (codeMatch[1]) {
          // Parse the JSON from the code block
          const jsonObj = JSON.parse(codeMatch[1]);
          
          // Check if this looks like a tool call
          if (jsonObj.tool && jsonObj.parameters) {
            console.log(`Found tool call for "${jsonObj.tool}":`, jsonObj.parameters);
            toolCalls.push({ 
              name: jsonObj.tool, 
              arguments: jsonObj.parameters as Record<string, unknown> 
            });
          }
        }
      } catch (e) {
        console.error("Error parsing JSON from code block:", e instanceof Error ? e.message : String(e));
      }
    }
    
    // If we found any tool calls in code blocks, return them
    if (toolCalls.length > 0) {
      console.log("Found tool calls in code blocks:", toolCalls);
      return toolCalls;
    }
    
    // Try to find function calls in the text using regex
    // Look for patterns like: {"name": "calculator", "arguments": {"a": 5, "b": 3, "operation": "add"}}
    // or {"function": "calculator", "parameters": {"a": 5, "b": 3, "operation": "add"}}
    
    // Second attempt: Look for tool calls in JSON format
    const jsonPattern = /\{(?:"name"|"function"):\s*"([^"]+)",\s*(?:"arguments"|"parameters"):\s*(\{[^}]+\})\}/g;
    let match;
    
    while ((match = jsonPattern.exec(text)) !== null) {
      console.log("Found JSON pattern match:", match[0]);
      // Ensure name is not undefined before proceeding
      if (match[1] && match[2]) {
        const name = match[1];
        try {
          // The second capture group is the arguments JSON
          const argsString = match[2];
          console.log(`Parsing arguments for tool "${name}":`, argsString);
          const args = JSON.parse(argsString) as Record<string, unknown>;
          toolCalls.push({ name, arguments: args });
        } catch (e) {
          console.error(`Error parsing arguments for tool "${name}":`, e instanceof Error ? e.message : String(e));
        }
      }
    }
    
    // If we found any tool calls using the JSON pattern, return them
    if (toolCalls.length > 0) {
      console.log("Found tool calls using JSON pattern:", toolCalls);
      return toolCalls;
    }
    
    // Third attempt: Try to find a JSON block in the text that might contain tool calls
    const fullJsonPattern = /\{[\s\S]*\}/g;
    const jsonMatches = text.match(fullJsonPattern);
    
    if (jsonMatches) {
      console.log("Found potential JSON blocks:", jsonMatches.length);
      
      for (const jsonText of jsonMatches) {
        try {
          console.log("Attempting to parse JSON block:", jsonText);
          const parsed = JSON.parse(jsonText);
          
          // Check if this is a tool call
          if (parsed.name && (parsed.arguments || parsed.parameters)) {
            console.log("Found tool call in JSON block:", parsed);
            const args = parsed.arguments || parsed.parameters;
            toolCalls.push({ name: parsed.name, arguments: args as Record<string, unknown> });
          } else if (parsed.function && (parsed.arguments || parsed.parameters)) {
            console.log("Found tool call with 'function' key:", parsed);
            const args = parsed.arguments || parsed.parameters;
            toolCalls.push({ name: parsed.function, arguments: args as Record<string, unknown> });
          } else if (parsed.tool && parsed.parameters) {
            console.log("Found tool call with 'tool' key:", parsed);
            toolCalls.push({ name: parsed.tool, arguments: parsed.parameters as Record<string, unknown> });
          }
        } catch (e) {
          console.log("Failed to parse JSON block:", e instanceof Error ? e.message : String(e));
        }
      }
      
      if (toolCalls.length > 0) {
        console.log("Found tool calls using JSON block parsing:", toolCalls);
        return toolCalls;
      }
    } else {
      console.log("No JSON blocks found in the text");
    }
    
    // If we haven't found any tool calls yet, try a more lenient approach
    console.log("No tool calls found with standard patterns, trying more lenient approach");
    
    // Return null if no tool calls were found
    return null;
  }

  /**
   * Create a stream generator that handles updates to the message
   */
  private async *createStreamGenerator(
    stream: AsyncIterable<TextGenerationStreamOutput>,
    message: Message
  ): AsyncGenerator<string, void> {
    let accumulatedText = "";
    
    try {
      for await (const chunk of stream) {
        const tokenText = chunk.token.text;
        
        // Update accumulated text
        accumulatedText += tokenText;
        
        // Update the message content
        const updatedMessage = new Message({
          role: "assistant",
          content: accumulatedText,
          cache: message.cache,
        });
        
        // Copy properties to the original message
        Object.assign(message, updatedMessage);
        
        // Yield the token text
        yield tokenText;
      }
      
      // After streaming completes, check for potential tool calls in the response
      // This ensures we don't try to parse incomplete JSON during streaming
      this.debug("Stream complete, checking for tool calls", { content: accumulatedText });
      
      const toolCalls = this.parseToolCallsFromText(accumulatedText);
      if (toolCalls && toolCalls.length > 0) {
        this.debug("Found tool calls in stream response", { toolCalls });
        
        // Create a new message with the tool calls
        const updatedMessage = new Message({
          role: "assistant",
          content: accumulatedText,
          toolCalls: toolCalls.map(call => ({
            toolUseId: `tool-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            name: call.name,
            arguments: call.arguments
          })),
          cache: message.cache,
        });
        
        // Replace the message
        Object.assign(message, updatedMessage);
      }
    } catch (error) {
      this.error("Error processing Hugging Face stream", { error });
      throw error;
    }
  }

  /**
   * Create a stream generator that handles updates to the message for chat completion
   */
  private async *createChatStreamGenerator(
    stream: AsyncIterable<TextGenerationStreamOutput>,
    message: Message
  ): AsyncGenerator<string, void> {
    let accumulatedText = "";
    
    try {
      for await (const chunk of stream) {
        const tokenText = chunk.token.text;
        
        // Update accumulated text
        accumulatedText += tokenText;
        
        // Update the message content
        const updatedMessage = new Message({
          role: "assistant", 
          content: accumulatedText,
          cache: message.cache,
        });
        
        // Copy properties to the original message
        Object.assign(message, updatedMessage);
        
        // Yield the token text
        yield tokenText;
      }
      
      // After streaming completes, check for potential tool calls in the response
      // This ensures we don't try to parse incomplete JSON during streaming
      this.debug("Stream complete, checking for tool calls", { content: accumulatedText });
      
      const toolCalls = this.parseToolCallsFromText(accumulatedText);
      if (toolCalls && toolCalls.length > 0) {
        this.debug("Found tool calls in stream response", { toolCalls });
        
        // Create a new message with the tool calls
        const updatedMessage = new Message({
          role: "assistant",
          content: accumulatedText,
          toolCalls: toolCalls.map(call => ({
            toolUseId: `tool-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            name: call.name,
            arguments: call.arguments
          })),
          cache: message.cache,
        });
        
        // Replace the message
        Object.assign(message, updatedMessage);
      }
    } catch (error) {
      this.error("Error processing Hugging Face chat stream", { error });
      throw error;
    }
  }
} 